import { configureAuth, type UniversalAuthConfig } from '@lastbrand/fe-common/universal-auth'

import getAmplifyConfig from '@src/modules/auth/amplify/amplify-config'
import { mapIdentityConfigToAuthConfig } from '@src/modules/auth/identity-config-api'
import {
  clearIdentityConfigCache,
  readAuthConfigFromCognitoTokens,
  readRawIdentityConfigCache,
} from '@src/modules/auth/identity-config-cache'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'

export type AuthErrorHandler = (error: Error, context: Record<string, unknown>) => void

/**
 * True when module-scope auth config was resolved from a user-specific source
 * (tokens in localStorage or the identity-config cache from a previous session).
 * When true, the subsequent `authConfigDataHolder.initialize` useEffect is
 * skipped to avoid overwriting the correct user-specific config with the
 * regional default from the identity API. When false (tier 3 static fallback
 * for anonymous users), the useEffect runs to fetch the correct regional
 * config (needed for flows like Google OAuth).
 */
let multiPoolConfigResolvedFromLocalSource = false

export function wasMultiPoolConfigResolvedFromLocalSource(): boolean {
  return multiPoolConfigResolvedFromLocalSource
}

/** Resets the internal flag. Intended for unit tests only. */
export function resetMultiPoolConfigResolvedFlagForTests(): void {
  multiPoolConfigResolvedFromLocalSource = false
}

/**
 * Resolves and applies the Amplify auth config at module scope. Uses a 3-tier
 * priority chain for multi-pool auth:
 *   1. Cognito tokens in localStorage (source of truth for a logged-in user)
 *   2. Identity config cache from a previous session
 *   3. Static fallback (anonymous / new user)
 *
 * When `multi_pool_auth_enabled` is off, the static config is applied directly.
 */
export function resolveAndConfigureAuth(onError: AuthErrorHandler): void {
  if (!getUserAgnosticGlobalFeatureGatesFromMemory().multi_pool_auth_enabled) {
    void configureAuth({ ...getAmplifyConfig(), onError } as UniversalAuthConfig)
    return
  }

  // Priority 1: Extract pool config directly from Cognito tokens in localStorage.
  // The JWT is the source of truth — `iss` gives region + poolId, `aud` gives clientId.
  const tokenConfig = readAuthConfigFromCognitoTokens()
  if (tokenConfig) {
    // Defensive: if a stale identity-config cache from a different pool exists
    // (e.g. previous WebView session with a different user), clear it so later
    // reads don't pick up the wrong pool.
    const cachedIdentity = readRawIdentityConfigCache()
    if (cachedIdentity && cachedIdentity.cognitoClientId !== tokenConfig.userPoolClientId) {
      clearIdentityConfigCache()
    }
    // Prefer the cached identity config's oauth.domain over the static fallback when the
    // pool matches — avoids pointing Google Sign-In at the wrong regional hosted UI.
    const baseConfig =
      cachedIdentity && cachedIdentity.cognitoClientId === tokenConfig.userPoolClientId
        ? mapIdentityConfigToAuthConfig(cachedIdentity)
        : getAmplifyConfig()
    void configureAuth({
      ...baseConfig,
      ...tokenConfig,
      onError,
    } as UniversalAuthConfig)
    multiPoolConfigResolvedFromLocalSource = true
    return
  }

  // Priority 2: Identity config cache from a previous session.
  // Configures Amplify synchronously for immediate use, but does NOT set
  // multiPoolConfigResolvedFromLocalSource: the cache may be from a different browse
  // region, so initialize() must still run to verify and correct the pool (including
  // oauth.domain for Google Sign-In). initialize() will cache-hit and skip the network
  // call when the cached regionId matches.
  const cachedIdentity = readRawIdentityConfigCache()
  if (cachedIdentity) {
    void configureAuth({
      ...mapIdentityConfigToAuthConfig(cachedIdentity),
      onError,
    } as UniversalAuthConfig)
    return
  }

  // Priority 3: Static fallback (anonymous / new user).
  void configureAuth({ ...getAmplifyConfig(), onError } as UniversalAuthConfig)
}
